import React from 'react';
import { cn } from '../utils/cn';
export const Textarea = React.forwardRef(function Textarea(
  {
    label,
    helperText,
    error,
    autoResize = false,
    showCharCount = false,
    className,
    id,
    maxLength,
    onChange,
    ...props
  },
  ref,
) {
  const inputId = id || React.useId();
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;
  const internalRef = React.useRef(null);
  const [charCount, setCharCount] = React.useState(
    props.value?.length || props.defaultValue?.length || 0,
  );
  const setRefs = React.useCallback(
    (node) => {
      internalRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );
  const handleResize = React.useCallback(() => {
    const el = internalRef.current;
    if (!el || !autoResize) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [autoResize]);
  const handleChange = React.useCallback(
    (e) => {
      if (showCharCount) setCharCount(e.target.value.length);
      handleResize();
      onChange?.(e);
    },
    [showCharCount, handleResize, onChange],
  );
  React.useEffect(() => {
    handleResize();
  }, [handleResize]);
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-fg-secondary">
          {label}
        </label>
      )}
      <textarea
        ref={setRefs}
        id={inputId}
        className={cn(
          'block w-full rounded-md border bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-tertiary transition-colors duration-150',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          error
            ? 'border-danger-500 focus:ring-danger-500/25'
            : 'border-edge focus:border-primary-500 focus:ring-primary-500/25',
          (props.disabled || props.readOnly) && 'bg-surface-secondary opacity-75 cursor-not-allowed',
          autoResize && 'resize-none overflow-hidden',
          className,
        )}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : helperText ? helperId : undefined}
        maxLength={maxLength}
        onChange={handleChange}
        {...props}
      />
      <div className="mt-1 flex justify-between">
        <div>
          {error && (
            <p id={errorId} className="text-sm text-danger-600" role="alert">
              {error}
            </p>
          )}
          {!error && helperText && (
            <p id={helperId} className="text-sm text-fg-tertiary">
              {helperText}
            </p>
          )}
        </div>
        {showCharCount && (
          <p className="text-xs text-fg-tertiary">
            {charCount}
            {maxLength != null && `/${maxLength}`}
          </p>
        )}
      </div>
    </div>
  );
});
